import type { CSSProperties, ReactNode } from 'react';
import { useReloadOntoLatestDeploy } from './useReloadOntoLatestDeploy';

/**
 * The update this build is blocked on: each app's `checkAppVersion` `requiredUpdate`, which
 * returns this shape. Never `NATIVE` on web: a web build has no native build.
 */
export type RequiredUpdate =
  | { type: 'NATIVE'; storeUrl: string | null }
  | { type: 'JS'; jsVersion: number | null };

type Props = {
  /** `null` or `undefined` (loading, failed, skipped) never blocks. */
  requiredUpdate: RequiredUpdate | null | undefined;
  children: ReactNode;
};

/**
 * Blocks a web build below its app's minimum JS version and reloads the page onto the latest
 * deploy once it is out (`useReloadOntoLatestDeploy`); otherwise renders `children`.
 * - Mount above every Convex subscription: the blocking screen replaces `children`, so their
 *   subscriptions unmount and queries whose shape changed can't throw.
 */
export const AppVersionGate = ({ requiredUpdate, children }: Props) =>
  requiredUpdate?.type === 'JS' ? (
    <JsUpdateRequired jsVersion={requiredUpdate.jsVersion} />
  ) : (
    children
  );

const JsUpdateRequired = ({ jsVersion }: { jsVersion: number | null }) => {
  useReloadOntoLatestDeploy(jsVersion);
  return (
    <div style={$container} data-testid="app-version-gate-js-required">
      <p style={$title}>Updating the app…</p>
    </div>
  );
};

const $container: CSSProperties = {
  alignItems: 'center',
  backgroundColor: '#444',
  display: 'flex',
  justifyContent: 'center',
  minHeight: '100dvh',
  padding: 32,
};

const $title: CSSProperties = {
  color: '#fff',
  fontSize: 22,
  fontWeight: 600,
  margin: 0,
  textAlign: 'center',
};
